import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Wallet2, ArrowUpCircle, ArrowDownCircle, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const EMPTY_FORM = { vendorId: '', type: 'topup', amount: '', date: new Date().toISOString().slice(0, 10), tripId: '', bookingRef: '', note: '' };

export default function VendorAdvances() {
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [statement, setStatement] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadSummary = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/accounting/vendor-advances/summary`);
      setSummary(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load vendor advance summary');
    }
  };

  const loadVendors = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/accounting/vendors`);
      setVendors(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load vendors');
    }
  };

  useEffect(() => { loadSummary(); loadVendors(); }, []);

  const loadStatement = async (vendorId) => {
    if (!vendorId) { setStatement(null); return; }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/accounting/vendor-advances`, { params: { vendor_id: vendorId } });
      setStatement(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load statement');
    }
  };

  const selectVendor = (vendorId) => {
    setSelectedVendor(vendorId);
    setForm((f) => ({ ...f, vendorId }));
    loadStatement(vendorId);
  };

  const submit = async () => {
    if (!form.vendorId) { toast.error('Pick a vendor'); return; }
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/accounting/vendor-advances`, {
        vendorId: form.vendorId, type: form.type, amount: amt, date: form.date,
        tripId: form.tripId, bookingRef: form.bookingRef, note: form.note,
      });
      toast.success(`${form.type === 'topup' ? 'Top-up' : 'Draw-down'} recorded`);
      setForm((f) => ({ ...f, amount: '', tripId: '', bookingRef: '', note: '' }));
      loadStatement(form.vendorId);
      loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record entry');
    } finally {
      setSaving(false);
    }
  };

  const rows = (statement && statement.entries) || [];

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="vendor-advances-page">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Wallet2 className="h-6 w-6 text-teal-600" /> Vendor Advance Balance
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          A simple running total per vendor for pre-paid deposits (airlines, OTAs, attraction operators, etc.)
          — not double-entry bookkeeping. A draw-down beyond the available balance is rejected, never silently negative.
        </p>
      </header>

      <Card className="overflow-x-auto" data-testid="vendor-advance-summary-table">
        <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b">Vendors with Advance Activity</div>
        {summary.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">No advance entries yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendor</TableHead>
                <TableHead className="text-right">Top-ups</TableHead>
                <TableHead className="text-right">Draw-downs</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.map((s) => (
                <TableRow key={s.vendorId} data-testid={`vendor-advance-row-${s.vendorId}`} className={selectedVendor === s.vendorId ? 'bg-teal-50' : ''}>
                  <TableCell className="text-sm">{s.vendorName}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-emerald-700">{inr(s.totalTopup)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-rose-700">{inr(s.totalDrawdown)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-bold">{inr(s.balance)}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => selectVendor(s.vendorId)} data-testid={`view-statement-${s.vendorId}`}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card className="p-4 space-y-3" data-testid="vendor-advance-entry-form">
        <h2 className="text-sm font-semibold text-slate-800">Record Top-up / Draw-down</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Vendor *</Label>
            <Select value={form.vendorId} onValueChange={selectVendor}>
              <SelectTrigger className="mt-1" data-testid="advance-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
              <SelectContent>
                {vendors.map((v) => (
                  <SelectItem key={v.vendorId} value={v.vendorId}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Type *</Label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger className="mt-1" data-testid="advance-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="topup">Top-up</SelectItem>
                <SelectItem value="drawdown">Draw-down</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Amount (₹) *</Label>
            <Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="mt-1" data-testid="advance-amount-input" />
          </div>
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="mt-1" data-testid="advance-date-input" />
          </div>
          <div>
            <Label className="text-xs">Trip ID (optional)</Label>
            <Input value={form.tripId} onChange={(e) => setForm((f) => ({ ...f, tripId: e.target.value }))} className="mt-1" data-testid="advance-trip-id-input" />
          </div>
          <div>
            <Label className="text-xs">Booking Ref (optional)</Label>
            <Input value={form.bookingRef} onChange={(e) => setForm((f) => ({ ...f, bookingRef: e.target.value }))} className="mt-1" data-testid="advance-booking-ref-input" />
          </div>
          <div className="col-span-2 md:col-span-3">
            <Label className="text-xs">Note</Label>
            <Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className="mt-1" data-testid="advance-note-input" />
          </div>
        </div>
        <Button onClick={submit} disabled={saving} className="gap-2 bg-teal-600 hover:bg-teal-700" data-testid="record-advance-btn">
          <Plus className="h-4 w-4" /> {saving ? 'Saving…' : 'Record Entry'}
        </Button>
      </Card>

      {statement && (
        <Card className="overflow-x-auto" data-testid="vendor-advance-statement-table">
          <div className="px-4 py-2 flex items-center justify-between border-b">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Statement</span>
            <span className="text-sm font-mono font-bold" data-testid="vendor-advance-statement-balance">Balance: {inr(statement.balance)}</span>
          </div>
          {rows.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">No entries for this vendor yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Trip / Ref</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Running Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.entryId} data-testid={`advance-entry-row-${r.entryId}`}>
                    <TableCell className="font-mono text-xs">{r.date}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold ${r.type === 'topup' ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {r.type === 'topup' ? <ArrowUpCircle className="h-3.5 w-3.5" /> : <ArrowDownCircle className="h-3.5 w-3.5" />}
                        {r.type === 'topup' ? 'Top-up' : 'Draw-down'}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.tripId || r.bookingRef || '—'}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(r.amount)}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold">{inr(r.runningBalance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}
